'use client';

import Skeleton from '@mui/material/Skeleton';
import type { SxProps, Theme } from '@mui/material/styles';
import { type ComponentType, useEffect, useState } from 'react';
import { loadCalculatorBySlug } from './loadCalculatorBySlug';

type CalculatorBySlugComponent = ComponentType<{ slug: string | null }>;

type Props = {
  slug: string | null;
};

// Heights measured on the interest calculator. Below 768px the fields sit on top of
// the chart. From 768px the block is the taller column: the fields, or the chart
// (min(50vw, 488px), same as the compounding chart) plus the result text around it.
// The bottom margin matches CalculatorLayout so the content under the block stays put.
const calculatorSkeletonSx: SxProps<Theme> = {
  width: '100%',
  height: {
    xs: 1102,
    sm: 'max(577px, calc(min(50vw, 488px) + 180px))',
  },
  mb: 4,
  '@media (min-width: 440px)': {
    marginBottom: 'calc(1.62385rem + 0.45872vw)',
  },
  '@media (min-width: 1312px)': {
    marginBottom: '32px',
  },
};

function CalculatorBlockSkeleton() {
  return <Skeleton variant="rectangular" animation={false} aria-hidden sx={calculatorSkeletonSx} />;
}

export function LazyCalculatorBySlug({ slug }: Props) {
  const [Calculator, setCalculator] = useState<CalculatorBySlugComponent | null>(null);

  useEffect(() => {
    let cancelled = false;
    void loadCalculatorBySlug()
      .then(loaded => {
        if (!cancelled) setCalculator(() => loaded);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (!Calculator) {
    return <CalculatorBlockSkeleton />;
  }

  return <Calculator slug={slug} />;
}
